import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * @param {HTMLFormElement} form
 * @returns {string[]}
 */
function getSelectedOptionValuesFromVariantForm(form) {
  const values = [];
  for (const child of form.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (child.tagName === 'FIELDSET') {
      const input = child.querySelector('input[type="radio"]:checked');
      if (input instanceof HTMLInputElement && input.value) values.push(input.value);
    } else if (child.classList.contains('variant-option--dropdowns')) {
      const select = child.querySelector('select.variant-option__select');
      if (select instanceof HTMLSelectElement && select.value) values.push(select.value);
    }
  }
  return values;
}

/**
 * @param {Array<{option1?: string | null, option2?: string | null, option3?: string | null, sku?: string | null}>} variants
 * @param {string[]} selectedValues
 */
function findVariantBySelectedOptions(variants, selectedValues) {
  if (!variants?.length || !selectedValues.length) return null;
  const n = selectedValues.length;
  return (
    variants.find((v) => {
      const vo = [v.option1, v.option2, v.option3].filter((x) => x != null && String(x) !== '');
      if (vo.length !== n) return false;
      return vo.every((val, i) => val === selectedValues[i]);
    }) ?? null
  );
}

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  /** @type {Element | null} */
  #listenerTarget = null;

  connectedCallback() {
    super.connectedCallback();
    // Match product-price.js: section ids use `product_information`, not `ProductInformation-`.
    this.#listenerTarget = this.closest('.shopify-section, dialog, product-card');
    if (!this.#listenerTarget) return;
    this.#listenerTarget.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
    this.#listenerTarget.addEventListener(ThemeEvents.variantSelected, this.updateSkuFromSelection);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#listenerTarget) {
      this.#listenerTarget.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
      this.#listenerTarget.removeEventListener(ThemeEvents.variantSelected, this.updateSkuFromSelection);
      this.#listenerTarget = null;
    }
  }

  /**
   * Updates SKU immediately from variant picker + embedded variants JSON (before section fetch completes).
   * @param {Event} event
   */
  updateSkuFromSelection = (event) => {
    const picker =
      event.target instanceof HTMLElement && event.target.matches('variant-picker')
        ? event.target
        : event.target instanceof HTMLElement
          ? event.target.closest('variant-picker')
          : null;
    if (!(picker instanceof HTMLElement)) return;
    if (picker.dataset.productId !== this.dataset.productId) return;

    const form = picker.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const scriptEl = picker.querySelector('script[type="application/json"][data-ft-product-variants]');
    if (!scriptEl?.textContent) return;

    /** @type {unknown} */
    let variants;
    try {
      variants = JSON.parse(scriptEl.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(variants)) return;

    const selected = getSelectedOptionValuesFromVariantForm(form);
    if (!selected.length) return;

    const variant = findVariantBySelectedOptions(
      /** @type {Array<{option1?: string | null, option2?: string | null, option3?: string | null, sku?: string | null}>} */ (
        variants
      ),
      selected
    );
    if (!variant) return;

    const variantSku = variant.sku || '';
    if (variantSku) {
      this.style.display = 'block';
      this.refs.sku.textContent = variantSku;
    } else {
      this.style.display = 'none';
      this.refs.sku.textContent = '';
    }
  };

  /**
   * Updates the SKU after section fetch + morph (authoritative).
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.resource) {
      const variantSku = event.detail.resource.sku || '';

      if (variantSku) {
        this.style.display = 'block';
        this.refs.sku.textContent = variantSku;
      } else {
        this.style.display = 'none';
        this.refs.sku.textContent = '';
      }
    }
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
